import { useCallback } from "react"
import { MoreHorizontal, Sparkles, BookmarkPlus, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { WinThemeEvidenceList } from "@/components/strategy/WinThemeEvidenceList"
import { ThemeEvidenceBadge } from "@/components/strategy/ThemeEvidenceBadge"
import { TextWithCitations, type CitationSource } from "@/components/drafting/CitationBadge"
import { usePreviewStore } from "@/stores/preview-store"
import { useStrategySettingsStore } from "@/stores/strategy-settings-store"
import { useAuthStore } from "@/stores/auth-store"
import type { WinTheme, ThemeSource } from "@/stores/strategy-store"
import type { ExtractedCriterion } from "@/types/extraction"

function toCitationSources(sources: ThemeSource[] | null): CitationSource[] {
  return (sources ?? []).map(s => ({
    documentId: s.documentId,
    documentName: s.documentName,
    pageNumber: s.pageNumber,
  }))
}

const categoryLabels: Record<string, string> = {
  experience: 'Experience',
  personnel: 'Personnel',
  methodology: 'Methodology',
  innovation: 'Innovation',
  relationship: 'Relationship',
  value: 'Value',
  risk: 'Risk Mitigation',
  local_presence: 'Local Presence',
  other: 'Other',
}

const strengthConfig: Record<string, { className: string }> = {
  strong: { className: 'bg-[hsl(var(--status-success)/0.1)] text-[hsl(var(--status-success))] border-transparent' },
  moderate: { className: 'bg-[hsl(var(--status-warning)/0.1)] text-[hsl(var(--status-warning))] border-transparent' },
  weak: { className: 'bg-muted text-muted-foreground border-transparent' },
}

interface WinThemeCardProps {
  theme: WinTheme
  index: number
  evalCriteria: ExtractedCriterion[]
  onEdit: () => void
  onRemove: () => void
  onRefine: () => void
  /** View-only reader: no actions menu. */
  readOnly?: boolean
}

function formatWeight(ec: ExtractedCriterion): string {
  if (ec.weightPercentage != null) return `${ec.weightPercentage}%`
  if (ec.weightPoints != null) return `${ec.weightPoints} pts`
  return ''
}

export function WinThemeCard({ theme, index, evalCriteria, onEdit, onRemove, onRefine, readOnly = false }: WinThemeCardProps) {
  const mappedCriteria = evalCriteria.filter(ec => ec.id && theme.evaluationCriteriaIds.includes(ec.id))
  const citationSources = toCitationSources(theme.sources)
  const role = useAuthStore(s => s.role)
  const isAdmin = role === 'admin'
  const addBankTheme = useStrategySettingsStore(s => s.addBankTheme)

  const handleNavigateToSource = useCallback((source: CitationSource) => {
    void usePreviewStore.getState().openPreview({
      documentId: source.documentId,
      documentName: source.documentName,
      ...(source.pageNumber != null && { page: source.pageNumber }),
      context: 'library',
      citationLabel: `Source: ${source.documentName}`,
    })
  }, [])

  const handleSaveToLibrary = useCallback(async () => {
    try {
      await addBankTheme({
        title: theme.title,
        description: theme.description,
        category: theme.category,
      })
      toast.success('Saved to win theme library')
    } catch {
      toast.error('Failed to save to library')
    }
  }, [addBankTheme, theme.title, theme.description, theme.category])

  return (
    <div className="border border-border rounded-xl bg-card p-5 shadow-sm group hover:border-[hsl(var(--brand)/0.3)] transition-colors">
      {/* Header */}
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2.5">
          <span className="text-xs font-semibold text-muted-foreground tabular-nums">{index + 1}</span>
          <h4 className="text-sm font-medium">{theme.title}</h4>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={strengthConfig[theme.strength]?.className}>
            {theme.strength.charAt(0).toUpperCase() + theme.strength.slice(1)}
          </Badge>
          {!readOnly && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={onEdit}>Edit theme</DropdownMenuItem>
              <DropdownMenuItem onClick={onRefine} className="text-[hsl(var(--ai-accent-text))] focus:text-[hsl(var(--ai-accent-text))] focus:bg-[hsl(var(--ai-accent-soft))]">
                <Sparkles className="h-3.5 w-3.5 mr-2" />
                Refine with AI
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem onClick={() => void handleSaveToLibrary()}>
                  <BookmarkPlus className="h-3.5 w-3.5 mr-2" />
                  Save to library
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onRemove} className="text-destructive focus:text-destructive">
                Delete theme
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </div>
      </div>

      {/* Category + source + evidence-strength */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-meta">{categoryLabels[theme.category] ?? theme.category}</span>
        {theme.source === 'ai' && (
          <>
            <span className="text-meta">&middot;</span>
            <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--ai-accent-text))] bg-[hsl(var(--ai-accent-soft))] px-1.5 py-0.5 rounded">
              <Sparkles className="h-2.5 w-2.5" />
              AI generated
            </span>
          </>
        )}
        {theme.evidenceStrength && (
          <ThemeEvidenceBadge strength={theme.evidenceStrength} count={theme.libraryEvidenceCount} />
        )}
        {theme.refineStatus === 'refining' && (
          <>
            <span className="text-meta">&middot;</span>
            <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--ai-accent-text))] bg-[hsl(var(--ai-accent-soft))] px-1.5 py-0.5 rounded">
              <Loader2 className="h-2.5 w-2.5 animate-spin" />
              Refining…
            </span>
          </>
        )}
      </div>

      {/* Description */}
      {theme.description && (
        <p className="text-sm text-foreground leading-relaxed mb-4">
          {citationSources.length > 0 ? (
            <TextWithCitations
              text={theme.description}
              sources={citationSources}
              onNavigateToSource={handleNavigateToSource}
            />
          ) : (
            theme.description
          )}
        </p>
      )}

      {/* Evidence */}
      {theme.evidence.length > 0 && (
        <div className="mb-3">
          <div className="text-xs font-medium text-muted-foreground mb-2">Evidence</div>
          <WinThemeEvidenceList
            evidence={theme.evidence}
            sources={citationSources}
            onNavigateToSource={handleNavigateToSource}
            readOnly
          />
        </div>
      )}

      {/* Mapped criteria */}
      {mappedCriteria.length > 0 && (
        <div>
          <div className="text-xs font-medium text-muted-foreground mb-2">Mapped to evaluation criteria</div>
          <div className="flex flex-wrap gap-1.5">
            {mappedCriteria.map(ec => (
              <span key={ec.id} className="inline-flex items-center px-2 py-1 rounded-lg text-xs bg-muted/30 border border-border">
                {ec.title}{formatWeight(ec) ? ` (${formatWeight(ec)})` : ''}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
